import React, { useEffect, useMemo } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';

import { useGetDimensionsList } from '@api/common/queryHooks';
import { EPanelOpenMethod } from '@components/block-event-single/store';
import LogicSelector from '@components/common/LogicSelector';
import Label from '@components/common/NumberLabel';
import { useQueryDimensionData } from '@components/mapping-panel/api';
import AddRuleButton from '@components/mapping-panel/components/AddRuleButton';
import { EInputType } from '@components/mapping-panel/components/RuleBuilder/KeyValueBuilder/typed';
import { CusSelect } from '@components/mapping-panel/components/RuleBuilder/MetadataRuleGroup/styled';
import { IMetadataRuleGroup } from '@components/mapping-panel/components/RuleBuilder/MetadataRuleGroup/typed';
import MetadataRuleRow from '@components/mapping-panel/components/RuleBuilder/MetadataRuleRow';
import {
  AddRuleButtonWrapper,
  EventName,
  InputBox,
  Main,
  Operator,
  Prefix,
  RuleList,
  TopBar,
  TopBarLeft,
} from '@components/mapping-panel/components/RuleBuilder/RuleGroup/styled';
import {
  CAN_BE_MAPPED_METADATA_DIMENSION_TYPE,
  EQUAL_ONLY_METADATA,
  letters,
} from '@components/mapping-panel/constants/constant';
import { useMappingPanelStore } from '@components/mapping-panel/store';
import { Icon } from '@components/metrics-builder/components/MetricList';
import DeleteActiveSvg from '@icons/mapping-panel/delete-active-icon.svg';
import DeleteSvg from '@icons/mapping-panel/delete-icon.svg';
import { getOperatorOptions } from '@src/emp-3.0/utils/buildOptions';
import { EStringOp } from '@src/emp-3.0/types';

const MetadataRuleGroup = ({
  mode,
  orIndex,
  subAndIndex,
  activeIndices,
  data,
  onDeleteMetadataGroup,
  onDeleteMetadataItem,
  onAddOrMetadataItem,
  updateActiveIndices,
  onChange,
  onDragEnd,
  showPadding,
}: IMetadataRuleGroup) => {
  const isViewMode = mode === EPanelOpenMethod.VIEW;

  const { operatorOptionsExcludingNull } = getOperatorOptions();

  const [needRefetchQueryKeys, setNeedRefetchQueryKeys] = useMappingPanelStore((state) => [
    state.needRefetchQueryKeys,
    state.setNeedRefetchQueryKeys,
  ]);

  const metadataKey = data?.[0]?.key;
  const label = letters[subAndIndex % letters.length ?? 0];
  const activeMetadataGroup =
    activeIndices?.orIndex === orIndex &&
    activeIndices?.metadataItems?.some(({ subAndIndex: andIndex }) => andIndex === subAndIndex) &&
    !isViewMode;

  const { data: dimensionList, isLoading: isDimensionListLoading } = useGetDimensionsList();
  const {
    data: dimensionData,
    isFetching: isDimensionDataLoading,
    refetch: dimensionRefetch,
  } = useQueryDimensionData(
    {
      fields: metadataKey ? [metadataKey] : [],
      filters: [],
      options: {
        enabled: !!metadataKey,
      },
    },
    500,
  );

  useEffect(() => {
    if (metadataKey && needRefetchQueryKeys.includes(metadataKey)) {
      dimensionRefetch();
      setNeedRefetchQueryKeys(metadataKey, 'delete');
    }
  }, [metadataKey]);

  const metadataNameList = useMemo(() => {
    return (dimensionList?.data ?? []).filter(
      (dimension) =>
        dimension?.can_be_mapped &&
        CAN_BE_MAPPED_METADATA_DIMENSION_TYPE.includes(dimension?.dimension_type),
    );
  }, [dimensionList?.data]);

  const metadataNameOptions = useMemo(
    () =>
      metadataNameList.map((canBeMappedDimension) => ({
        label: canBeMappedDimension?.display_name,
        value: canBeMappedDimension?.dimension_name,
      })),
    [metadataNameList],
  );

  const metadataName = metadataNameList.find(
    (dimension) => dimension?.dimension_name === metadataKey,
  )?.display_name;

  const isAllMetadataRulesEmpty =
    metadataName !== undefined && data?.every((rule) => rule.value.length === 0);

  const handleMetadataNameChange = (value) => {
    onChange?.(-1, EInputType.METADATA_NAME, value);
    if (EQUAL_ONLY_METADATA.includes(value)) {
      data?.forEach((item, index) => {
        if (item.op !== EStringOp.EQ && item.op !== EStringOp.NOT_EQ) {
          onChange?.(index, EInputType.METADATA_OPERATOR, EStringOp.EQ);
        }
      });
    }
  };

  const handelDragEnd = (...args) => {
    onDragEnd?.(args[0], args[1]);
  };

  let count = 0;
  const items: JSX.Element[] =
    data?.map((orMetadata, subOrIndex) => {
      count++;
      const rowLabel = `${label}.${count}`;
      const hasBorderBottom = data?.length - 1 !== subOrIndex;
      const itemActiveIndices = activeIndices?.metadataItems?.find(
        (i) => i.subAndIndex === subAndIndex && i.subOrIndex === subOrIndex,
      );
      return (
        <MetadataRuleRow
          id={`metadata_rule_${orIndex + 1}.${subAndIndex + 1}.${subOrIndex + 1}`}
          disabled={isViewMode}
          key={rowLabel}
          index={subOrIndex}
          rowLabel={rowLabel}
          isNoRules={isAllMetadataRulesEmpty}
          itemActiveIndices={activeMetadataGroup ? itemActiveIndices : undefined}
          showLogicOperator={subOrIndex !== 0}
          dimensionName={metadataName}
          data={orMetadata}
          options={dimensionData}
          operatorOptions={operatorOptionsExcludingNull}
          loading={isDimensionDataLoading}
          onActive={() => updateActiveIndices?.(false, [{ subAndIndex, subOrIndex }])}
          onDelete={() => {
            onDeleteMetadataItem?.(subOrIndex);
            updateActiveIndices?.(false, [{ subAndIndex }]);
          }}
          onChange={(type, value) => onChange?.(subOrIndex, type, value)}
          style={hasBorderBottom ? undefined : { borderBottom: 'none' }}
        />
      );
    }) ?? [];

  const droppableId = JSON.stringify({ orIndex, subAndIndex, key: 'Metadata' });
  const droppableArea = (
    <Droppable droppableId={droppableId} key={droppableId}>
      {(provided, snapshot) => (
        <RuleList
          ref={provided.innerRef}
          {...provided.droppableProps}
          isDraggingOver={snapshot.isDraggingOver}
        >
          {items}
          {provided.placeholder}
        </RuleList>
      )}
    </Droppable>
  );

  return (
    <DragDropContext onDragEnd={handelDragEnd}>
      <div
        onClick={(e) => {
          e?.stopPropagation();
          updateActiveIndices?.(false, [{ subAndIndex }]);
        }}
      >
        <TopBar>
          <TopBarLeft>
            <Prefix type="Group">
              <LogicSelector disabled={isViewMode} value="AND" active={activeMetadataGroup} />
              <Label value={label} square active={activeMetadataGroup} />
            </Prefix>
            <InputBox>
              <EventName active={activeMetadataGroup}>Metadata Name *</EventName>
              <Operator>=</Operator>
              <CusSelect
                data-test-id={`metadata_name_${orIndex + 1}.${subAndIndex + 1}`}
                disabled={isViewMode}
                showSearch
                active={activeMetadataGroup && activeIndices?.metadataName}
                showArrow={activeMetadataGroup && activeIndices?.metadataName}
                placeholder="Please select..."
                dropdownMatchSelectWidth={190}
                // placeholder is not displayed because metadataKey is initialized with '' instead of undefined.
                value={metadataKey || undefined}
                options={metadataNameOptions}
                onChange={handleMetadataNameChange}
                loading={isDimensionListLoading}
                onFocus={() => {
                  updateActiveIndices?.(true, [{ subAndIndex }]);
                }}
                onBlur={() => {
                  updateActiveIndices?.(false, [{ subAndIndex }]);
                }}
                onClick={(e) => e?.stopPropagation()}
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
                }
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
              />
            </InputBox>
          </TopBarLeft>
          {!isViewMode && (
            <Icon
              style={{ marginRight: '8px' }}
              component={DeleteSvg}
              activeComponent={DeleteActiveSvg}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteMetadataGroup?.();
              }}
            />
          )}
        </TopBar>
        <Main showPadding={showPadding}>
          {droppableArea}
          {activeMetadataGroup ? (
            <AddRuleButtonWrapper>
              <AddRuleButton
                text="Add OR Rule"
                onClick={() => onAddOrMetadataItem?.(metadataKey)}
              />
            </AddRuleButtonWrapper>
          ) : null}
        </Main>
      </div>
    </DragDropContext>
  );
};

export default MetadataRuleGroup;
